import api from "./api";

export const CEP_ERROR_CODES = {
  INVALID_FORMAT: "CEP_INVALID_FORMAT",
  NOT_FOUND: "CEP_NOT_FOUND",
  RATE_LIMITED: "CEP_RATE_LIMITED",
  UNAVAILABLE: "CEP_UNAVAILABLE",
  CANCELLED: "CEP_CANCELLED",
};

export class CepLookupError extends Error {
  constructor(code, message, options = {}) {
    super(message);
    this.name = "CepLookupError";
    this.code = code;
    this.status = options.status;
    this.retryAfterSeconds = options.retryAfterSeconds ?? null;
  }
}

export const normalizeCep = (value) => String(value ?? "").replace(/\D/g, "");

export const formatCep = (value) => {
  const digits = normalizeCep(value).slice(0, 8);

  return digits.length > 5
    ? `${digits.slice(0, 5)}-${digits.slice(5)}`
    : digits;
};

const toNullableText = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value !== "string") {
    throw new CepLookupError(
      CEP_ERROR_CODES.UNAVAILABLE,
      "A consulta de CEP retornou um formato inesperado.",
      { status: 503 },
    );
  }

  return value.trim() || null;
};

const normalizeCepAddress = (address, requestedCep) => {
  const responseCep = normalizeCep(address?.zipCode);
  const city = toNullableText(address?.city);
  const uf = toNullableText(address?.uf)?.toUpperCase() || null;

  if (responseCep !== requestedCep || !city || !uf) {
    throw new CepLookupError(
      CEP_ERROR_CODES.UNAVAILABLE,
      "A consulta de CEP retornou um formato inesperado.",
      { status: 503 },
    );
  }

  return {
    zipCode: requestedCep,
    street: toNullableText(address?.street),
    district: toNullableText(address?.district),
    city,
    uf,
  };
};

const parseRetryAfter = (headers) => {
  const rawValue = headers?.["retry-after"] ?? headers?.get?.("retry-after");
  const seconds = Number.parseInt(rawValue, 10);

  return Number.isInteger(seconds) && seconds >= 0 ? seconds : null;
};

export const isCepLookupCancelled = (error) =>
  error?.code === "ERR_CANCELED" ||
  error?.code === CEP_ERROR_CODES.CANCELLED ||
  error?.name === "CanceledError" ||
  error?.name === "AbortError";

export const describeCepLookupError = (error) => {
  if (isCepLookupCancelled(error)) {
    return {
      code: CEP_ERROR_CODES.CANCELLED,
      status: null,
      retryAfterSeconds: null,
      retryable: false,
      message: "Consulta de CEP cancelada.",
    };
  }

  if (error instanceof CepLookupError) {
    return {
      code: error.code,
      status: error.status ?? null,
      retryAfterSeconds: error.retryAfterSeconds,
      retryable: error.code === CEP_ERROR_CODES.UNAVAILABLE,
      message: error.message,
    };
  }

  const status = error?.response?.status;
  const retryAfterSeconds = parseRetryAfter(error?.response?.headers);

  if (status === 400) {
    return {
      code: CEP_ERROR_CODES.INVALID_FORMAT,
      status,
      retryAfterSeconds: null,
      retryable: false,
      message: "Informe um CEP com oito dígitos.",
    };
  }

  if (status === 404) {
    return {
      code: CEP_ERROR_CODES.NOT_FOUND,
      status,
      retryAfterSeconds: null,
      retryable: false,
      message: "CEP não encontrado. Informe o endereço manualmente.",
    };
  }

  if (status === 429) {
    return {
      code: CEP_ERROR_CODES.RATE_LIMITED,
      status,
      retryAfterSeconds,
      retryable: true,
      message: retryAfterSeconds === null
        ? "Limite de consultas de CEP atingido. Aguarde para tentar novamente."
        : `Limite de consultas de CEP atingido. Aguarde ${retryAfterSeconds} segundos para tentar novamente.`,
    };
  }

  return {
    code: CEP_ERROR_CODES.UNAVAILABLE,
    status: status ?? 503,
    retryAfterSeconds: null,
    retryable: true,
    message: "Consulta de CEP indisponível no momento. Tente novamente.",
  };
};

export const lookupCepAddress = async (value, { signal } = {}) => {
  const zipCode = normalizeCep(value);

  if (zipCode.length !== 8) {
    throw new CepLookupError(
      CEP_ERROR_CODES.INVALID_FORMAT,
      "Informe um CEP com oito dígitos.",
      { status: 400 },
    );
  }

  const response = await api.get(`/addresses/cep/${zipCode}`, { signal });
  return normalizeCepAddress(response.data, zipCode);
};
